import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { userApis } from '../apis';

const initialState = {
  data: {
    consumerId: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    notifyByEmail: false,
    notifyBySms: false,
  },
  loading: 'idle',
};

export const fetchAccountDetails = createAsyncThunk(
  'user/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userApis.getAccountDetails();
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        return rejectWithValue(error.response.data.error);
      } else {
        throw error;
      }
    }
  }
);

export const updateAccountDetails = createAsyncThunk(
  'user/update',
  async accountDetailsBody => {
    const response = await userApis.updateAccountDetails({
      email: accountDetailsBody.email,
      firstName: accountDetailsBody.firstName,
      lastName: accountDetailsBody.lastName,
    });
    return response.data;
  }
);

export const updateCommunicationPreferences = createAsyncThunk(
  'user/updateCommunicationPreferences',
  async (field, { getState }) => {
    const {
      accountDetails: {
        data: { notifyByEmail, notifyBySms },
      },
    } = getState();

    const response = await userApis.updateCommunicationDetails({
      notifyByEmail,
      notifyBySms,
      ...field,
    });

    return response.data;
  }
);

const deleteAccount = createAsyncThunk(
  'user/delete',
  async (_, { rejectWithValue }) => {
    try {
      const result = await userApis.deleteAccount();
      return result?.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      } else {
        throw error;
      }
    }
  }
);

export const getAccountDetailsData = state => state.accountDetails.data;

const getAccountDetailsLoading = state => state.accountDetails.loading;

export const getAccountDetailsFields = createSelector(
  [getAccountDetailsData],
  ({ email, phone, firstName, lastName }) => ({
    email,
    phone,
    firstName,
    lastName,
  })
);

export const getCommunicationPreferences = createSelector(
  [getAccountDetailsData],
  ({ notifyByEmail, notifyBySms }) => ({
    notifyByEmail,
    notifyBySms,
  })
);

export const isPhoneNumberExistSelector = createSelector(
  [getAccountDetailsData],
  ({ phone }) => !!phone
);

export const isAccountDetailsLoading = createSelector(
  [getAccountDetailsLoading],
  loading => loading === 'pending'
);

const slice = createSlice({
  name: 'accountDetails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchAccountDetails.fulfilled, (state, action) => {
      state.data = { ...action.payload };
    });
    builder
      .addCase(updateAccountDetails.pending, state => {
        state.loading = 'pending';
      })
      .addCase(updateAccountDetails.fulfilled, (state, action) => {
        state.data = { ...action.payload };
        state.loading = 'idle';
      })
      .addCase(updateAccountDetails.rejected, state => {
        state.loading = 'idle';
      });
    builder
      .addCase(updateCommunicationPreferences.pending, state => {
        state.loading = 'pending';
      })
      .addCase(updateCommunicationPreferences.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = 'idle';
      })
      .addCase(updateCommunicationPreferences.rejected, state => {
        state.loading = 'idle';
      });
  },
});

export default {
  reducer: slice.reducer,
  actions: {
    fetchAccountDetails,
    updateAccountDetails,
    updateCommunicationPreferences,
    deleteAccount,
  },
  selectors: {
    getAccountDetailsData,
    getAccountDetailsFields,
    getCommunicationPreferences,
    isAccountDetailsLoading,
    isPhoneNumberExistSelector,
  },
};
